import { useMediaQuery, useTheme } from '@material-ui/core';
import { Auth } from 'aws-amplify';
import React from 'react';

import { ContextProps, RouteContext } from 'src/context';
import { LoadingWrapper } from 'src/legacy/components/Loading';
import { OnboardingAuthLayoutProps } from 'src/legacy/components/Onboarding/Login/OnboardingAuthLayout';
import { OnboardingFormCard } from 'src/legacy/components/Onboarding/OnboardingFormCard';
import { OnboardingRegisterForm } from 'src/legacy/components/Onboarding/OnboardingForms/OnboardingRegisterForm';
import { OnboardingPageLayout } from 'src/legacy/components/Onboarding/OnboardingPageLayout';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BrandAvatar } from 'src/legacy/components/UI/Avatars/BrandAvatar';
import { GetSignInMetadata, GetSignInMetadataInput } from 'src/utils/AuthUtils';

type OnboardingRegisterPageProps = OnboardingAuthLayoutProps;

export const OnboardingRegisterPage: React.FC<OnboardingRegisterPageProps> = ({
  onAuthComplete,
}) => {
  const [pageLoading, setPageLoading] = React.useState(false);
  const [isNewPasswordRequired, setIsNewPasswordRequired] =
    React.useState(false);
  const [authUser, setAuthUser] = React.useState(null);
  const context: ContextProps = React.useContext(RouteContext);

  /**
   * Get the initial email state which will be used to auto sign in the user
   */
  const [initialEmail] = React.useState(context?.query?.email || '');

  const handlePageLoading = (isLoading: boolean) => setPageLoading(isLoading);

  // auto sign in user with the email and password available in the query
  const autoSignIn = async (userEmail: string, password: string) => {
    try {
      handlePageLoading(true);
      const username = userEmail;
      const user = await Auth.signIn(
        username,
        password,
        GetSignInMetadata(context.query as GetSignInMetadataInput),
      );

      handlePageLoading(false);
      if (user) {
        setAuthUser(user);
        if (user && user.challengeName === 'NEW_PASSWORD_REQUIRED') {
          // once the user is logged using query email and password
          // show register form to update the password
          setIsNewPasswordRequired(true);
        }
      }
    } catch (error) {
      handlePageLoading(false);
    }
  };

  // if we have email and password in query, auto sign in user
  React.useEffect(() => {
    const password = context?.query?.auth || '';
    if (initialEmail && password) autoSignIn(initialEmail, password);
  }, []);

  const theme = useTheme();
  const isNonMobileWindow = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    <OnboardingPageLayout>
      <LoadingWrapper
        hideContentWhileLoading
        isLoading={pageLoading}
        color={isNonMobileWindow ? 'white' : undefined}
      >
        <OnboardingFormCard>
          {isNewPasswordRequired && (
            <>
              <BrandAvatar />
              <div className="mt-3 mb-10 text-center px-2 sm:px-0">
                <BaseTypography fontType="18Medium">
                  Activate your account
                </BaseTypography>
              </div>
              <OnboardingRegisterForm
                authUser={authUser}
                onAuthComplete={onAuthComplete}
              />
            </>
          )}
        </OnboardingFormCard>
      </LoadingWrapper>
    </OnboardingPageLayout>
  );
};
